import { PropsWithChildren, useState } from 'react';
import { useI18n } from 'next-localization';

import { ProviderLocation } from '@/features/profile/profile.types';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { useProviderLocation } from '@/hooks/use-provider-location';
import { cn } from '@/lib/utils';
import { NeuIcon, NeuModal } from '@hcahealthcaredpx/neutron-react';
import { AzureMap } from '@xsf/shared-lib/components/azure-map';
import { HCALocation } from '@xsf/shared-lib/types/search';

import { LinkButton } from '../link-button';
import { LocationsList } from './locations-list';
import styles from './modal.module.css';

export const ChangeLocationModal = ({
  children,
  locations,
  providerId,
  icon,
  showLinkButton = true,
  changeLocationCallback,
}: PropsWithChildren<{
  locations: ProviderLocation[];
  providerId: string;
  icon?: string;
  showLinkButton?: boolean;
  changeLocationCallback?: (newLocation: Pick<ProviderLocation, 'id' | 'mhdCalendarId'>) => void;
}>): JSX.Element => {
  const { t } = useI18n();
  const { setActiveProviderLocationByDoctorId } = useDistanceContext();

  const [show, setShow] = useState(false);

  const mappedLocations: HCALocation[] = locations.map((location) => ({
    facilityId: location.id,
    locationName: location.name,
    longitude: location.longitude,
    latitude: location.latitude,
    marketedFacilityName: location.name,
    distance: Number(location.distance),
    coid: location.hospitalAffiliationCOID,
    //  Needed to show the pin
    marketedSecondaryTypeNames: [''],
    marketedPrimaryPhoneNumber: location.phone,
    city: location.city,
    postalCode: location.zip,
    state: location.state,
    street1: location.street,
  }));

  const isSingleLocation = locations.length === 1;

  const getProviderActiveLocation = useProviderLocation();
  const selectedLocationId = getProviderActiveLocation(locations, providerId)?.id;

  const chooseLocationHandler = (location: ProviderLocation) => {
    setActiveProviderLocationByDoctorId((prev) => {
      const updatedLocations = {
        ...prev,
        [providerId]: location.id,
      };

      sessionStorage.setItem('activeProviderLocations', JSON.stringify(updatedLocations));

      return updatedLocations;
    });

    if (changeLocationCallback) {
      changeLocationCallback({ id: location.id, mhdCalendarId: location.mhdCalendarId });
    }
  };

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.buttonWrapper}>
        {showLinkButton && (
          <LinkButton
            onClick={() => setShow((p) => !p)}
            className={cn('d-flex px-0 d-flex align-items-center', styles.linkButton)}
            type="button"
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-expect-error */}
            {icon && <NeuIcon name={icon} />}
            <span>{children}</span>
          </LinkButton>
        )}

        {!showLinkButton && <span onClick={() => setShow((p) => !p)}>{children}</span>}
      </div>

      <NeuModal
        active={show}
        size="large"
        onNeuDismiss={() => setShow((p) => !p)}
        label={isSingleLocation ? t('OAS.Listing.LocationDetails') : t('Header.ChangeLocation')}
        backdropDismiss
      >
        <NeuModal.Content>
          <div className="row pb-2">
            <div className="col-12 col-sm-6 pe-0">
              <div className={cn(styles.azureMap, 'w-100 overflow-hidden d-none d-sm-block')}>
                <AzureMap locations={mappedLocations} />
              </div>
            </div>
            <div className="col-12 col-sm-6 ps-0">
              <LocationsList
                locations={locations}
                chooseLocationHandler={chooseLocationHandler}
                closeModal={() => setShow(false)}
                selectedId={selectedLocationId ?? ''}
              />
            </div>
          </div>
        </NeuModal.Content>
      </NeuModal>
    </div>
  );
};
